import { defineStore } from 'pinia';
import type {
  Application,
  DevAccess,
  DevStats,
  DevTicket,
  DevTicketDetail,
  DevTicketFilters,
  PortMap,
  RepoInsight,
  CreateDevTicketInput,
  UpdateDevTicketInput,
} from '@its/shared';
import { api } from '@/lib/api';

interface State {
  access: DevAccess | null;
  applications: Application[];
  tickets: DevTicket[];
  stats: DevStats | null;
  ports: PortMap | null;
  /** Vue GitHub, indexée par application : on ne la recharge pas à chaque onglet. */
  repos: Record<string, RepoInsight>;
  filters: DevTicketFilters;
  loading: boolean;
  loaded: boolean;
  loadingTickets: boolean;
  loadingPorts: boolean;
}

/**
 * Module 5 — espace de développement (0052).
 *
 * DEUX CONVENTIONS DE LA MAISON, tenues ici :
 *
 *  - `access` vient de l'API et n'est JAMAIS recalculé côté écran. Réécrire la
 *    règle de droit dans le front, c'est se condamner à la voir diverger du
 *    serveur — ce qui est déjà arrivé avec les rôles globaux, restés dans six
 *    écrans après leur retrait des comptes (0045).
 *  - le squelette de chargement ne s'affiche qu'au PREMIER chargement ; les
 *    rafraîchissements suivants se font en place, sans clignotement.
 */
export const useDevStore = defineStore('dev', {
  state: (): State => ({
    access: null,
    applications: [],
    tickets: [],
    stats: null,
    ports: null,
    repos: {},
    // On ouvre sur ce qui reste à traiter : une liste de tout, close comprise,
    // est une liste où le travail du jour ne se voit pas.
    filters: { ouverts: true },
    loading: false,
    loaded: false,
    loadingTickets: false,
    loadingPorts: false,
  }),

  getters: {
    /** Ouvre l'entrée de menu et l'écran — la fonction développeur, seule. */
    canOpen: (s): boolean => s.access?.canOpen === true,
    /** Vue d'ensemble : référentiel, dépôts, ports, synthèse. */
    canView: (s): boolean => s.access?.canView === true,
    isDeveloper: (s): boolean => s.access?.isDeveloper === true,
    canManage: (s): boolean => s.access?.canManageApplications === true,
    /** Supprime une fiche — fonction développeur ; la cascade emporte les remontées. */
    canDelete: (s): boolean => s.access?.canDeleteApplications === true,
    canReport: (s): boolean => s.access?.canReport === true,
    /** Issues, jalons, pull requests, commits GitHub — fonction développeur SEULE. */
    canUseGithub: (s): boolean => s.access?.canUseGithub === true,
    /** Écrire sur GitHub : développeur, jeton d'écriture, hors mode découverte (T2). */
    canWriteGithub: (s): boolean => s.access?.canWriteGithub === true,
    /** Pourquoi on ne peut pas écrire, quand on devrait pouvoir — affiché, jamais deviné. */
    githubWriteReason: (s): string | null => s.access?.githubWriteReason ?? null,
    canLinkGithub: (s): boolean => s.access?.canLinkGithub === true,
    /** Remontées ouvertes, tous périmètres confondus — pastille du menu. */
    ouverts: (s): number => s.applications.reduce((n, a) => n + a.ticketsOuverts, 0),
    /** Bugs critiques non corrigés : le seul chiffre qui mérite du rouge. */
    critiques: (s): number => s.applications.reduce((n, a) => n + a.bugsCritiques, 0),
    application:
      (s) =>
      (id: string): Application | undefined =>
        s.applications.find((a) => a.id === id),
  },

  actions: {
    /**
     * Position de l'appelant. Appelée au niveau de l'application (App.vue) pour
     * décider de l'entrée de menu : la connaître au montage de la page serait
     * trop tard, le menu est déjà dessiné.
     */
    async fetchAccess(): Promise<void> {
      try {
        const { access } = await api.get<{ access: DevAccess }>('/dev/access');
        this.access = access;
      } catch {
        // Pas de droit, pas d'entrée : un échec ici ne doit pas casser la nav.
        this.access = null;
      }
    },

    async fetchApplications(): Promise<void> {
      /*
       * `canReport` et non `canOpen` : le testeur n'ouvre plus le module
       * (2026-09-01) mais il a toujours besoin de la liste — bornée à ses
       * projets par l'API — pour que sa remontée sache à quelle application se
       * rattacher. Garder `canOpen` ici aurait vidé le formulaire de l'espace
       * testeur au moment même où on lui retirait l'entrée de menu.
       */
      if (!this.access?.canOpen && !this.access?.canReport) return;
      this.loading = !this.loaded;
      try {
        const { applications } = await api.get<{ applications: Application[] }>('/dev/applications');
        this.applications = applications;
        this.loaded = true;
      } finally {
        this.loading = false;
      }
    },

    async fetchTickets(patch: Partial<DevTicketFilters> = {}): Promise<void> {
      this.filters = { ...this.filters, ...patch };
      this.loadingTickets = true;
      try {
        const query = new URLSearchParams();
        for (const [key, value] of Object.entries(this.filters)) {
          // Un filtre vide ne doit pas partir : `?nature=` ferait rendre 400 par
          // le schéma d'entrée, qui n'accepte que des valeurs de l'énumération.
          if (value === undefined || value === null || value === '') continue;
          query.set(key, String(value));
        }
        const suffix = query.toString();
        const { tickets } = await api.get<{ tickets: DevTicket[] }>(
          `/dev/tickets${suffix ? `?${suffix}` : ''}`,
        );
        this.tickets = tickets;
      } finally {
        this.loadingTickets = false;
      }
    },

    async fetchStats(applicationId?: string): Promise<void> {
      const { stats } = await api.get<{ stats: DevStats }>(
        `/dev/stats${applicationId ? `?applicationId=${applicationId}` : ''}`,
      );
      this.stats = stats;
    },

    /**
     * Ports des deux VM. Chargés À LA DEMANDE : la lecture traverse plusieurs
     * fichiers d'Azure-Infra via l'API GitHub, ce qu'on ne fait pas au montage
     * d'un écran dont c'est le troisième onglet.
     */
    async fetchPorts(force = false): Promise<void> {
      if (this.ports && !force) return;
      this.loadingPorts = true;
      try {
        const { ports } = await api.get<{ ports: PortMap }>('/dev/ports');
        this.ports = ports;
      } finally {
        this.loadingPorts = false;
      }
    },

    async fetchRepo(applicationId: string, force = false): Promise<RepoInsight | null> {
      if (this.repos[applicationId] && !force) return this.repos[applicationId]!;
      const { repo } = await api.get<{ repo: RepoInsight }>(
        `/dev/applications/${applicationId}/repo`,
      );
      this.repos = { ...this.repos, [applicationId]: repo };
      return repo;
    },

    async createTicket(input: CreateDevTicketInput): Promise<DevTicketDetail> {
      const { ticket } = await api.post<{ ticket: DevTicketDetail }>('/dev/tickets', input);
      // Mise à jour en place : la remontée apparaît en tête sans recharger.
      this.tickets = [ticket, ...this.tickets];
      await this.fetchApplications();
      return ticket;
    },

    async updateTicket(id: string, input: UpdateDevTicketInput): Promise<DevTicketDetail> {
      const { ticket } = await api.patch<{ ticket: DevTicketDetail }>(`/dev/tickets/${id}`, input);
      this.tickets = this.tickets.map((t) => (t.id === id ? { ...t, ...ticket } : t));
      /*
       * Une remontée close SORT de la liste quand on ne regarde que l'ouvert.
       * La laisser affichée après l'avoir corrigée donnerait à croire que le
       * clic n'a rien fait, et un rechargement complet ferait clignoter tout
       * l'écran pour une seule ligne.
       */
      if (this.filters.ouverts && !['nouveau', 'en_cours', 'a_tester'].includes(ticket.status)) {
        this.tickets = this.tickets.filter((t) => t.id !== id);
      }
      await this.fetchApplications();
      return ticket;
    },

    async createApplication(input: Record<string, unknown>): Promise<Application> {
      const { application } = await api.post<{ application: Application }>(
        '/dev/applications',
        input,
      );
      this.applications = [...this.applications, application].sort((a, b) =>
        a.name.localeCompare(b.name, 'fr'),
      );
      return application;
    },

    async updateApplication(id: string, input: Record<string, unknown>): Promise<Application> {
      const { application } = await api.patch<{ application: Application }>(
        `/dev/applications/${id}`,
        input,
      );
      this.applications = this.applications.map((a) => (a.id === id ? application : a));
      // La fiche a pu changer de dépôt ou de branche : la vue GitHub en cache
      // ne décrit alors plus la bonne chose.
      const { [id]: _obsolete, ...rest } = this.repos;
      this.repos = rest;
      return application;
    },

    /**
     * Suppression de la fiche — et, en cascade côté base, de ses remontées.
     *
     * La liste est amputée EN PLACE plutôt que rechargée : l'appelant quitte la
     * fiche dans la foulée, et un rechargement complet ferait clignoter l'écran
     * d'arrivée pour une ligne en moins. La vue GitHub en cache part avec elle,
     * sans quoi une fiche recréée sur le même dépôt hériterait de l'ancienne.
     */
    async deleteApplication(id: string): Promise<void> {
      await api.del(`/dev/applications/${id}`);
      this.applications = this.applications.filter((a) => a.id !== id);
      const { [id]: _obsolete, ...rest } = this.repos;
      this.repos = rest;
    },
  },
});
